import { Suspense, lazy } from "react";
import { Container } from "react-bootstrap";
import { useSelector } from "react-redux";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";

import Header from "./components/Header";
import RestaurantHero from "./components/RestaurantHero";
import Footer from "./components/Footer";
import Loader from "./components/Loader";
import ScrollToHash from "./components/ScrollToHash";
import ScrollToTop from "./components/ScrollToTop";
import GoogleAnalytics from "./components/GoogleAnalytics";
import MenuFonts from "./components/MenuFonts";
import { deriveAccentTokens } from "./utils/contrastColor";

import LandingScreen from "./screens/LandingScreen";
import ProductScreen from "./screens/ProductScreen";
import ProductDetailScreen from "./screens/ProductDetailScreen";
import CartScreen from "./screens/CartScreen";
import OrderSuccessScreen from "./screens/OrderSuccessScreen";
import ReservationHubScreen from "./screens/ReservationHubScreen";
import ReservationSuccessScreen from "./screens/ReservationSuccessScreen";
import ReservationLookupScreen from "./screens/ReservationLookupScreen";

// Code-split: the homepage is the conversion-critical path, these four
// pages aren't — no reason to ship their JS in the initial bundle.
const AboutScreen = lazy(() => import("./screens/AboutScreen"));
const ContactScreen = lazy(() => import("./screens/ContactScreen"));
const PrivacyScreen = lazy(() => import("./screens/PrivacyScreen"));
const TermsScreen = lazy(() => import("./screens/TermsScreen"));

// Wolt-style product sheet: opened in-app (Product.js/ProductScreen.js pass
// `state: { backgroundLocation }` when navigating to /products/:id), the
// underlying route keeps rendering the menu list as the dimmed backdrop
// while a second, unlocated <Routes> renders ProductDetailScreen on top as
// an overlay. A direct/shared link to /products/:id (no state — no menu
// list to render behind it) falls through to the *first* Routes' own
// /products/:id match instead, so ProductDetailScreen still works as a
// plain full page in that case (see its own isOverlay check).
function AppRoutes({ isLanding, isRestaurant }) {
  const location = useLocation();
  const background = location.state?.backgroundLocation;

  return (
    <>
      <Routes location={background || location}>
        {/* LANDING */}
        {isLanding && (
          <>
            <Route path="/" element={<LandingScreen />} />
            <Route path="/about" element={<AboutScreen />} />
            <Route path="/contact" element={<ContactScreen />} />
            <Route path="/privacy" element={<PrivacyScreen />} />
            <Route path="/terms" element={<TermsScreen />} />
          </>
        )}

        {/* RESTAURANT MENU */}
        {isRestaurant && (
          <>
            <Route path="/" element={<ProductScreen />} />
            <Route path="/products/:id" element={<ProductDetailScreen />} />
            <Route path="/cart" element={<CartScreen />} />
            <Route path="/order-success" element={<OrderSuccessScreen />} />
            <Route path="/reservation" element={<ReservationHubScreen />} />
            <Route path="/reservations/success" element={<ReservationSuccessScreen />} />
            <Route path="/reservations/:token" element={<ReservationLookupScreen />} />
          </>
        )}

        {/* FALLBACK */}
        <Route
          path="*"
          element={isRestaurant ? <ProductScreen /> : <LandingScreen />}
        />
      </Routes>

      {isRestaurant && background && (
        <Routes>
          <Route path="/products/:id" element={<ProductDetailScreen />} />
        </Routes>
      )}
    </>
  );
}

function App() {
  const host = window.location.hostname;

  const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(host);

  const parts = host.split(".");
  const hasSubdomain = parts.length > 2 && !isIP;

  const subdomain = hasSubdomain ? parts[0] : null;

  const isAdmin = subdomain === "app";
  const isLanding = !subdomain;
  const isRestaurant = subdomain && !isAdmin;

  // Restaurant subdomains get the owner's brand color threaded through
  // every surface that otherwise reads Tablify's own fixed tokens
  // (category pills, buttons, prices — see tokens.css) via a CSS custom
  // property override scoped to this wrapper only. Landing/admin are
  // untouched — deriveAccentTokens returns null there, and null style
  // is a no-op div wrapper.
  const { restaurant: themedRestaurant = {} } = useSelector(
    (state) => state.restaurantDetails || {},
  );
  const accentStyle = isRestaurant
    ? deriveAccentTokens(themedRestaurant.accent_color)
    : null;

  return (
    <Router>
      <ScrollToTop />
      <ScrollToHash />
      {isLanding && <GoogleAnalytics />}
      {isRestaurant && <MenuFonts />}
      <div style={accentStyle || undefined}>
        {isLanding && <Header />}
        {isRestaurant && <RestaurantHero />}

        <main className="py-3">
          <Container>
            <Suspense fallback={<Loader />}>
              <AppRoutes isLanding={isLanding} isRestaurant={isRestaurant} />
            </Suspense>
          </Container>
        </main>

        {isLanding && <Footer />}
      </div>
    </Router>
  );
}

export default App;
